import React, {Fragment, useCallback, useEffect, useState} from "react";
import Frame from "../Frame";
import api from "../../api";
import {endpoints} from "../../config";
import {useSelector} from "react-redux";
import {selectDatabaseName} from "../../reducers/client";
import {TimePeriodFilters} from "../HierarchyFilter/HierarchyFilter";
import Button from "../ui/Button";
import {Spinner} from "../ui/Spinner";
import {useParams} from "react-router-dom";

type Status = "set" | "changed" | "unset"

interface Reminder {
  timePeriod: keyof TimePeriodFilters
  timePeriodInterval: number
  readonly lastEmail?: string
  readonly nextEmail?: string,
  status: Status
}

// const range = (start: number, end: number) => Array.from({length: (end - start)}, (v, k) => k + start);

const defaultReminder: Reminder = {
  timePeriod: "Date1",
  timePeriodInterval: 1,
  status: "unset"
}

const defaultLanguage: Language = {
  id: 1,
  languageCode: "en",
  languageNameEnglish: "English",
  languageNameNatural: "English",
  status: "unset"
}

interface Language {
  id: number
  languageCode: string,
  languageNameNatural: string,
  languageNameEnglish: string
  status: Status
}

interface Settings {
  language: Language,
  reminder: Reminder
}

const defaultSettings = {reminder: defaultReminder, language: defaultLanguage}

const Settings: React.FC = () => {
  const {projectId} = useParams();
  const showQuickFilter = false;
  const topclass = showQuickFilter ? "WidthContent" : "WidthContent HeightContent";
  const databaseName = useSelector(selectDatabaseName);

  const [languages, setLanguages] = useState<Language[]>([defaultLanguage]);
  const [currentReminder, setCurrentReminder] = useState<Reminder>(defaultReminder);
  const [currentLanguage, setCurrentLanguage] = useState<Language>(defaultLanguage);
  const [originalSettings, setOriginalSettings] = useState<Settings>(defaultSettings);
  const [dates, setDates] = useState<TimePeriodFilters>();
  const [showReminder, setShowReminder] = useState(false);

  // const { Reminder: currentReminder, Language: currentLanguage } = currentSettings
  // const { Reminder: originalReminder, Language: originalLanguage } = currentSettings

  const getSettings = useCallback(async () => {
    const {data} = await api.get(`${endpoints.settings.getSettings}?projectId=${projectId}&databaseName=${databaseName}`);
    if (data.details !== null) {
      if (data.details.reminder) {
        setCurrentReminder({
          ...data.details.reminder,
          status: "set"
        });
      }
      if (data.details.language) {
        setCurrentLanguage({
          ...data.details.language,
          status: "set"
        });
      }
      setOriginalSettings(data.details);
    }
  }, [databaseName, projectId]);

  const deleteReminder = useCallback(async () => {
    const response = await api.get(`${endpoints.settings.deleteReminder}?projectId=${projectId}&databaseName=${databaseName}`);
    if (response.status === 200) {
      setCurrentReminder(defaultSettings.reminder);
    }
  }, [databaseName, projectId]);

  const getDates = useCallback(async () => {
    const {data} = await api.get(`${endpoints.settings.getDates}?projectId=${projectId}&databaseName=${databaseName}`);
    setDates(data.details.timePeriodFilters);
  }, [databaseName, projectId]);

  const getLanguages = useCallback(async () => {
    const {data} = await api.get(`${endpoints.settings.getLanguages}`);
    setLanguages(data.details);
  }, []);

  const validateReminder = useCallback((reminder: Reminder) => {
    return reminder.timePeriodInterval >= 1;
  }, []);

  const getShowReminder = useCallback(async () => {
    const {data} = await api.get(`${endpoints.settings.showReminder}?projectId=${projectId}`)
    setShowReminder(data.details);
  }, [projectId])

  const saveSettings = useCallback(async () => {
    const currentSettings: Settings = {
      reminder: currentReminder,
      language: currentLanguage
    }
    if (JSON.stringify(originalSettings) !== JSON.stringify(currentSettings)) {
      if (!validateReminder(currentReminder)) {
        console.warn("Reminder invalid")
      } else {
        const response = await api.post(`${endpoints.settings.setSettings}?projectId=${projectId}&databaseName=${databaseName}`, currentSettings);
        if (response.status === 200) {
          setReminderSetStatus("set");
          setLanguageSetStatus("set");
          alert("Saved Settings");
        } else {
          alert("Saving Settings Failed");
        }
      }
    }
  }, [projectId, databaseName, currentLanguage, currentReminder, originalSettings]);

  const resetSettings = useCallback(() => {
    setCurrentReminder(originalSettings.reminder);
    setCurrentLanguage(originalSettings.language);
  }, [originalSettings]);

  const setReminderSetStatus = React.useCallback((status: Status) => {
    setCurrentReminder(oldState => {
      return {
        ...oldState,
        status: status
      }
    });
  }, []);

  const setLanguageSetStatus = React.useCallback((status: Status) => {
    setCurrentLanguage(oldState => {
      return {
        ...oldState,
        status: status
      }
    });
  }, []);


  useEffect(() => {
    getDates().then(() => console.log("Dates Success")).catch(err => console.error(err));
    getLanguages().then(() => console.log("Languages Success")).catch(err => console.error(err));
    getShowReminder().then(() => console.log("Show Reminder Success")).catch(err => console.error(err));
    getSettings().then(() => console.log("Settings Success")).catch(err => console.error(err));
  }, [])

  const setLanguage = useCallback((stringId: string) => {
    setCurrentLanguage((oldLanguage) => {
      const intId = parseInt(stringId);
      const foundLang = languages.find(lang => lang.id === intId);
      if (foundLang) {
        setLanguageSetStatus("changed");
        return foundLang;
      } else {
        return oldLanguage;
      }
    })
  }, [languages]);

  const setTimePeriodInterval = useCallback((interval: number) => {
    setCurrentReminder((oldReminder) => {
      return {...oldReminder, timePeriodInterval: interval, status: "changed"}
    });
  }, []);

  const setTimePeriod = useCallback((period: keyof TimePeriodFilters) => {
    setCurrentReminder((oldReminder) => {
      return {...oldReminder, timePeriod: period, status: "changed"}
    });
  }, []);

  let {id: languageId, status: languageStatus} = currentLanguage;
  let {timePeriod, timePeriodInterval, status: reminderStatus} = currentReminder;

  let reminderStatusElement = <Fragment></Fragment>;
  switch (reminderStatus) {
    case "unset":
      reminderStatusElement = <h4 style={{color: "red", textAlign: "center", marginBottom: 0}}>Reminder Unset</h4>
      break;
    case "changed":
      reminderStatusElement = <h4 style={{color: "orange", textAlign: "center", marginBottom: 0}}>Reminder Unsaved</h4>
      break;
    case "set":
      reminderStatusElement = <h4 style={{color: "green", textAlign: "center", marginBottom: 0}}>Reminder Set</h4>
      break;
  }

  let languageStatusElement = <Fragment></Fragment>;
  switch (languageStatus) {
    case "unset":
      languageStatusElement = <h4 style={{color: "red", textAlign: "center", marginBottom: 0}}>Language Unset</h4>
      break;
    case "changed":
      languageStatusElement = <h4 style={{color: "orange", textAlign: "center", marginBottom: 0}}>Language Unsaved</h4>
      break;
    case "set":
      languageStatusElement = <h4 style={{color: "green", textAlign: "center", marginBottom: 0}}>Language Set</h4>
      break;
  }

  if (dates && languages && currentReminder && currentLanguage) {
    return <Frame
      userAccessField="bCanAccessDashboard"
    >
      <div className={topclass}>
        <h1>Settings</h1>
        <div className={"LoginForm"}>
          <h2>Language Settings</h2>
          <form>
            <div className={"FormBox"}>
              <label className={"FormLabel"}>Language
                {languageStatusElement}
                <select className={"EditBox"} style={{"WebkitAppearance": "menulist"}}
                        value={languageId}
                        onChange={e => setLanguage(e.currentTarget.value)}>
                  {languages.map((lang) => <option
                    key={lang.id}
                    value={lang.id}>{lang.languageNameEnglish}
                  </option>)}
                </select>
              </label>
            </div>
          </form>
        </div>
        {/*currentReminder ? <h3>{JSON.stringify(currentReminder)}</h3> : <h3>No reminder set</h3>*/}
        {showReminder && <div className={"LoginForm"}>
            <h2>Email Reminder Settings</h2>
          {reminderStatusElement}
            <form>
                <div className={"FormBox"}>
                    <label className={"FormLabel"}>Every
                        <input className={"EditBox"} value={timePeriodInterval} type={"number"}
                               min={1}
                               onChange={e => setTimePeriodInterval(parseInt(e.currentTarget.value))}/>
                        <select className={"EditBox"} style={{"WebkitAppearance": "menulist"}}
                                value={timePeriod}
                                onChange={e => setTimePeriod(e.currentTarget.value as keyof TimePeriodFilters)}>
                          {Object.entries(dates).map(([key, timePeriodFilter], i) => {
                            return <option key={i} value={key}>{timePeriodFilter?.displayName}</option>
                          })}
                        </select>
                    </label>
                </div>
            </form>
            <Button variety={"full"} style={{fontSize: 15}} onClick={deleteReminder}>Delete Reminder</Button>
        </div>}
        <div className={"LoginForm"}>
          <Button variety={"full"} style={{fontSize: 15, marginBottom: 10}} onClick={saveSettings}>Save
            Settings</Button>
          <Button variety={"full"} style={{fontSize: 15, marginBottom: 10}} onClick={resetSettings}>Reset
            Settings</Button>
        </div>
      </div>
    </Frame>
  } else return <Spinner/>
}

export default Settings;